import {
  ComponentIds,
  Entity,
  HierarchyUtils,
  SelectedComponent,
  ViewerObjectComponent
} from '@inter-ikea-core/core';
import { SelectionOverrideComponent } from '@inter-ikea-storageone/range';
import { reportSelectBlueDot } from 'analytics/reporters';
import { useCore } from 'contexts/core';
import { getProductEntities } from 'ducks/core';
import { getActiveWidget } from 'ducks/mobile';
import { getShowSelectionOverrideIndicators } from 'ducks/selectionOverride';
import usePrevious from 'hooks/usePrevious';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Indicator from 'ui/Indicator';
import SkapaTooltip from 'ui/Tooltip/SkapaTooltip';

const PositionWrapper = styled.div`
  position: absolute;
  top: ${({ theme }) => `calc(-${theme.space.xl} / 2)`};
  left: ${({ theme }) => `calc(-${theme.space.xl} / 2)`};
  margin-left: -15px;
  margin-top: -15px;
`;

export default function SelectionOverrideIndicators() {
  const coreApi = useCore();
  const entityManager = coreApi.getServices().entityManager;
  const showIndicators = useSelector(getShowSelectionOverrideIndicators);
  const [indicators, setIndicators] = useState<any>([]);
  const prevShowIndicators = usePrevious(showIndicators);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [selectableEntities, setSelectableEntities] = useState<Entity[]>([]);
  const previousSelectableEntities = usePrevious(selectableEntities);
  const activeWidget = useSelector(getActiveWidget);
  const productsInScene = useSelector(getProductEntities);
  const { formatMessage } = useIntl();

  const updateIndicators = useCallback(() => {
    if (showIndicators) {
      const entitiesWithPosition = selectableEntities
        .filter(filterVisibleEntity)
        .map(frame => coreApi.entities.getWithPositionById(frame.id))
        .filter(fwp => filterOutOfBoundIndicators(fwp?.position));

      setIndicators(entitiesWithPosition);
    }
  }, [selectableEntities, coreApi.entities, showIndicators]);

  const getSelectableEntitesInScene = useCallback(() => {
    const selectableEntites = coreApi.entities.getByComponent(SelectionOverrideComponent.id);
    setSelectableEntities(selectableEntites);
    updateIndicators();
  }, [coreApi.entities, updateIndicators]);

  useEffect(() => {
    updateIndicators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsInScene]);

  useEffect(() => {
    entityManager.subscribeAdd(SelectionOverrideComponent.id, () => getSelectableEntitesInScene());
    entityManager.subscribeRemove(SelectionOverrideComponent.id, () =>
      getSelectableEntitesInScene()
    );
    return () => {
      entityManager.unsubscribeAdd(SelectionOverrideComponent.id, getSelectableEntitesInScene);
      entityManager.unsubscribeRemove(SelectionOverrideComponent.id, getSelectableEntitesInScene);
    };
  }, [entityManager, getSelectableEntitesInScene]);

  useEffect(() => {
    entityManager.subscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, (entity: Entity) =>
      setSelectedEntity(entity)
    );
    entityManager.subscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, () =>
      setSelectedEntity(null)
    );
    return () => {
      entityManager.unsubscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, setSelectedEntity);
      entityManager.unsubscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, setSelectedEntity);
    };
  }, [entityManager]);

  const handleClick = useCallback(
    (entityId: number) => {
      const clickedEntity = coreApi.entities.find(entityId);
      clickedEntity?.addComponent(new SelectedComponent());
      reportSelectBlueDot();
    },
    [coreApi]
  );

  const filterVisibleEntity = (entity: Entity) => {
    if (entity.hasComponent(ComponentIds.ASSEMBLY_STRUCTURE_COMPONENT_ID)) {
      return HierarchyUtils.children(entity).some(childEntity => {
        const viewerObjectComponent = childEntity.getComponent<ViewerObjectComponent>(
          ComponentIds.VIEWER_OBJECT_COMPONENT_ID
        );
        return viewerObjectComponent?.viewerObject?.getVisible();
      });
    }
    return entity
      .getComponent<ViewerObjectComponent>(ComponentIds.VIEWER_OBJECT_COMPONENT_ID)
      ?.viewerObject?.getVisible();
  };

  const filterOutOfBoundIndicators = (
    position: { x: number | undefined; y: number | undefined } | undefined
  ) => {
    return !!(position?.x && position?.y);
  };

  useEffect(() => {
    return coreApi.subscribe('update', updateIndicators);
  }, [coreApi, updateIndicators]);

  useEffect(() => {
    if (
      selectableEntities.length !== previousSelectableEntities?.length ||
      showIndicators !== prevShowIndicators
    ) {
      updateIndicators();
    }
  }, [
    updateIndicators,
    showIndicators,
    prevShowIndicators,
    selectableEntities.length,
    previousSelectableEntities?.length,
    coreApi.entities
  ]);

  if (!showIndicators || selectedEntity || activeWidget === 'interiors') return null;

  return indicators.map(({ entityId, position }: any) => (
    <PositionWrapper
      key={entityId}
      style={{
        left: position.x + `px`,
        top: position.y + `px`
      }}
    >
      <SkapaTooltip
        tooltipText={formatMessage({ id: 'storageone.selectedProductListCustomiseFrame' })}
        tooltipButtonLabel={formatMessage({ id: 'storageone.selectedProductListCustomiseFrame' })}
        interactiveCustomTrigger
      >
        <Indicator
          aria-describedby={entityId}
          data-testid={`selection-override-indicator-${entityId}`}
          onClick={() => handleClick(entityId)}
          tabIndex={0}
          aria-label={formatMessage({ id: 'storageone.selectedProductListCustomiseFrame' })}
        />
      </SkapaTooltip>
    </PositionWrapper>
  ));
}
